// B-50 Phase 0 (23 Oct 2026) — the colour rules for the auto-built sponsor ad
// (spec rev 4 §3). Pure: no DOM, no canvas. The staff preview page draws the
// logo to an offscreen canvas and hands the RGBA pixels in here; the node test
// (adColours.test.mjs, run with `node --test`) feeds synthetic pixels.
//
//   1. Background = the dominant colour of the logo's BORDER pixels.
//      Transparent pixels are ignored; a logo whose border is mostly
//      transparent gets white.
//   2. Text = charcoal (#2C2B28) or white, whichever has the higher WCAG
//      contrast against that background. Below 4.5:1 the text sits on a soft
//      translucent panel, made just strong enough to reach 4.5:1.

export const CHARCOAL = '#2C2B28';
export const WHITE = '#FFFFFF';
export const AA_TEXT = 4.5;

// A pixel counts as "there" at alpha ≥ 128 of 255; anything fainter is the
// transparent background. Partly transparent pixels are composited on white.
const ALPHA_SOLID = 128;
// If fewer than half the border pixels are solid, the logo sits on a
// transparent background → white (spec §3.1).
const MIN_SOLID_SHARE = 0.5;

export function clampByte(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

export function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function rgbToHex(rgb) {
  return '#' + rgb.map(v => clampByte(v).toString(16).padStart(2, '0')).join('').toUpperCase();
}

// WCAG 2.x relative luminance of an sRGB colour.
export function relativeLuminance(rgb) {
  const lin = rgb.map(v => {
    const c = clampByte(v) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

export function contrastRatio(a, b) {
  const la = relativeLuminance(typeof a === 'string' ? hexToRgb(a) : a);
  const lb = relativeLuminance(typeof b === 'string' ? hexToRgb(b) : b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// Source-over blend of `top` at `alpha` (0–1) on an opaque `bottom`.
export function blend(bottom, top, alpha) {
  return bottom.map((v, i) => v * (1 - alpha) + top[i] * alpha);
}

/**
 * The dominant colour around the edge of an image.
 * @param {Uint8ClampedArray|number[]} data RGBA, row-major (canvas ImageData.data)
 * @param {number} width
 * @param {number} height
 * @param {{ band?: number }} [opts] border thickness in pixels (default ~2% of
 *   the shorter side, at least 1)
 * @returns {{ hex: string, transparent: boolean, solidShare: number }}
 *   transparent → the caller uses white (hex is already '#FFFFFF').
 */
export function edgeColour(data, width, height, opts = {}) {
  const w = Math.floor(width);
  const h = Math.floor(height);
  if (!data || w < 1 || h < 1 || data.length < w * h * 4) {
    return { hex: WHITE, transparent: true, solidShare: 0 };
  }
  const band = Math.max(1, Math.min(
    Math.floor(Math.min(w, h) / 2) || 1,
    opts.band || Math.round(Math.min(w, h) * 0.02) || 1
  ));

  // Bucket by 4 bits per channel (4096 buckets) so anti-aliasing and JPEG
  // noise don't split one background into many near-identical colours; the
  // winner is the average of the pixels in its bucket.
  const buckets = new Map();
  let total = 0;
  let solid = 0;
  const take = (x, y) => {
    const i = (y * w + x) * 4;
    total += 1;
    const a = data[i + 3];
    if (a < ALPHA_SOLID) return;
    solid += 1;
    const k = a / 255;
    // Composite on white, so a 70%-opaque red border reads as the pink it shows.
    const r = data[i] * k + 255 * (1 - k);
    const g = data[i + 1] * k + 255 * (1 - k);
    const b = data[i + 2] * k + 255 * (1 - k);
    const key = ((clampByte(r) >> 4) << 8) | ((clampByte(g) >> 4) << 4) | (clampByte(b) >> 4);
    const s = buckets.get(key);
    if (s) { s.n += 1; s.r += r; s.g += g; s.b += b; }
    else buckets.set(key, { n: 1, r, g, b });
  };
  for (let y = 0; y < h; y += 1) {
    const edgeRow = y < band || y >= h - band;
    for (let x = 0; x < w; x += 1) {
      if (edgeRow || x < band || x >= w - band) take(x, y);
    }
  }

  const solidShare = total ? solid / total : 0;
  if (solidShare < MIN_SOLID_SHARE || buckets.size === 0) {
    return { hex: WHITE, transparent: true, solidShare };
  }
  let best = null;
  for (const s of buckets.values()) if (!best || s.n > best.n) best = s;
  return {
    hex: rgbToHex([best.r / best.n, best.g / best.n, best.b / best.n]),
    transparent: false,
    solidShare,
  };
}

/**
 * Charcoal or white text on `bgHex`, whichever contrasts more; the ratio; and,
 * when neither reaches 4.5:1, a translucent panel to put the text on.
 * @returns {{ text: string, ratio: number, other: { text: string, ratio: number },
 *   panel: null | { colour: string, alpha: number, css: string, ratio: number } }}
 */
export function pickTextColour(bgHex) {
  const bg = hexToRgb(bgHex) || hexToRgb(WHITE);
  const onCharcoal = contrastRatio(bg, hexToRgb(CHARCOAL));
  const onWhite = contrastRatio(bg, hexToRgb(WHITE));
  const charcoalWins = onCharcoal >= onWhite;
  const text = charcoalWins ? CHARCOAL : WHITE;
  const ratio = charcoalWins ? onCharcoal : onWhite;
  const other = charcoalWins ? { text: WHITE, ratio: onWhite } : { text: CHARCOAL, ratio: onCharcoal };

  let panel = null;
  if (ratio < AA_TEXT) {
    // Charcoal text → a pale panel; white text → a dark one. Start soft and
    // strengthen in small steps until the text clears 4.5:1 on the blend.
    const panelRgb = charcoalWins ? hexToRgb(WHITE) : hexToRgb(CHARCOAL);
    let alpha = 0.5;
    let panelRatio = contrastRatio(blend(bg, panelRgb, alpha), hexToRgb(text));
    while (panelRatio < AA_TEXT && alpha < 0.95) {
      alpha = Math.round((alpha + 0.05) * 100) / 100;
      panelRatio = contrastRatio(blend(bg, panelRgb, alpha), hexToRgb(text));
    }
    panel = {
      colour: rgbToHex(panelRgb),
      alpha,
      css: `rgba(${panelRgb.join(', ')}, ${alpha})`,
      ratio: panelRatio,
    };
  }
  return { text, ratio, other, panel };
}

// "4.62:1" — rounded DOWN, so a 4.497 never reads as a pass.
export function formatRatio(r) {
  return `${(Math.floor(r * 100) / 100).toFixed(2)}:1`;
}

// Stuart, 29 Oct: "logos need to be more prominent". Many logo files carry a
// wide empty margin, so the mark looks small in the ad. contentBox finds the
// rectangle that actually has ink — pixels that aren't (nearly) transparent
// and, when the logo sits on a solid colour, aren't (nearly) that colour —
// so the page can crop the margin away. Returns { x, y, w, h } or null when
// there's no ink at all (then don't crop).
export function contentBox(data, width, height, { bgHex = null, tolerance = 40, alphaMin = 24 } = {}) {
  const w = Math.floor(width);
  const h = Math.floor(height);
  if (!data || w < 1 || h < 1 || data.length < w * h * 4) return null;
  const bg = bgHex ? hexToRgb(bgHex) : null;
  const tol2 = tolerance * tolerance;
  let minX = w; let minY = h; let maxX = -1; let maxY = -1;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4;
      const a = data[i + 3];
      if (a < alphaMin) continue;
      if (bg) {
        const k = a / 255;
        const dr = data[i] * k + 255 * (1 - k) - bg[0];
        const dg = data[i + 1] * k + 255 * (1 - k) - bg[1];
        const db = data[i + 2] * k + 255 * (1 - k) - bg[2];
        if (dr * dr + dg * dg + db * db <= tol2) continue;
      }
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}
